import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classes from "./SDGDetail.module.css";
import { BaseURL } from "../home/BaseData";

const SDG_COLORS = {
  1: "#FF7A00",
  2: "#EAB308",
  3: "#22C55E",
  4: "#EF4444",
  5: "#EC4899",
  6: "#3B82F6",
  7: "#F97316",
  8: "#06B6D4",
  9: "#64748B",
  10: "#F59E0B",
  11: "#8B5CF6",
  12: "#84CC16",
  13: "#64748B",
  14: "#0284C7",
  15: "#10B981",
  16: "#F43F5E",
  17: "#14B8A6",
};

const TEXT = {
  loading: { uz: "Yuklanmoqda...", en: "Loading...", ru: "Загрузка..." },
  home: { uz: "Bosh sahifa", en: "Home", ru: "Главная" },
  sdg: { uz: "SDG", en: "SDG", ru: "ЦУР" },
  notFoundTitle: {
    uz: "Ma'lumot topilmadi",
    en: "Not found",
    ru: "Материал не найден",
  },
  notFoundText: {
    uz: "Siz qidirayotgan SDG maqsadi mavjud emas yoki o'chirilgan.",
    en: "The SDG goal you are looking for does not exist or has been removed.",
    ru: "Запрашиваемая цель не существует или была удалена.",
  },
  more: { uz: "Batafsil", en: "Learn more", ru: "Подробнее" },
  soon: {
    uz: "Ma'lumot tez kunda joylanadi.",
    en: "Content will be published soon.",
    ru: "Материал будет опубликован в ближайшее время.",
  },
};

/** Matnni xatboshilarga ajratadi — admin oddiy matn kiritadi. */
const paragraphs = (text) =>
  (text || "")
    .split(/\n{1,}/)
    .map((t) => t.trim())
    .filter(Boolean);

const SDGDetail = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchGoalDetail = async () => {
      setLoading(true);
      setError(false);
      try {
        const urlsToTry = [
          `${BaseURL.replace(/\/$/, "")}/api/sdg/${identifier}/`,
          `http://127.0.0.1:8001/api/sdg/${identifier}/`,
          `http://127.0.0.1:8000/api/sdg/${identifier}/`,
          `http://localhost:8000/api/sdg/${identifier}/`,
          `http://localhost:8001/api/sdg/${identifier}/`
        ];
        let response = null;
        for (const url of urlsToTry) {
          try {
            response = await axios.get(url);
            if (response && response.data && response.data.goal) break;
          } catch (e) {
            // try next
          }
        }

        if (response && response.data && response.data.goal) {
          setGoal(response.data.goal);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching SDG detail:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (identifier) {
      fetchGoalDetail();
    }
  }, [identifier]);

  const lang = i18n.language || "uz";
  const T = (key) => TEXT[key][lang] || TEXT[key].uz;

  const L = (obj, prefix) => {
    if (!obj) return "";
    return obj[`${prefix}_${lang}`] || obj[`${prefix}_uz`] || "";
  };

  const mediaUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${BaseURL.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  if (loading) {
    return <div className={classes.loading}>{T("loading")}</div>;
  }

  if (error || !goal) {
    return (
      <div className={classes.narrow}>
        <div className={classes.errorBox}>
          <h2 className={classes.errorTitle}>{T("notFoundTitle")}</h2>
          <p>{T("notFoundText")}</p>
        </div>
      </div>
    );
  }

  const accent = goal.color || SDG_COLORS[goal.number] || "#133654";
  const title = L(goal, "header_title") || L(goal, "title");
  const heroImage = mediaUrl(goal.hero_image);
  const heroTagline = L(goal, "hero_tagline");
  const heroLead = L(goal, "hero_lead");
  const content = L(goal, "content");
  const contentHeading = L(goal, "content_heading");
  const author = L(goal, "author");
  const sections = (goal.sections || []).filter(
    (s) => L(s, "title") || L(s, "lead") || L(s, "panel_title") || (s.items || []).length
  );

  // Ko'rinish ham, raqamlash ham MAZMUNDAN chiqariladi — admin tanlamaydi.
  // Birinchi blok kirish bo'lib raqamsiz turadi, qolganlari 01, 02, 03 …
  // Oxirgi blokda rasm, panel va element bo'lmasa — u yakuniy chaqiriq bo'lib chiziladi.
  const hasBody = (s) =>
    Boolean(s.image) ||
    Boolean(L(s, "panel_title")) ||
    Boolean(L(s, "panel_body")) ||
    (s.items || []).length > 0;

  let counter = 0;
  const numbers = sections.map((s, i) => (i === 0 ? null : ++counter));
  const closingIndex =
    sections.length > 1 && !hasBody(sections[sections.length - 1])
      ? sections.length - 1
      : -1;

  const renderItems = (section) => {
    const items = section.items || [];
    const cards = items.filter((i) => i.kind === "card");
    const chips = items.filter((i) => i.kind === "chip");
    const listItems = items.filter((i) => i.kind === "list");

    return (
      <>
        {chips.length > 0 && (
          <ul className={classes.chipRow}>
            {chips.map((c) => (
              <li key={c.id} className={classes.chip}>
                {L(c, "title")}
              </li>
            ))}
          </ul>
        )}

        {listItems.length > 0 && (
          <ol className={classes.numList}>
            {listItems.map((item, i) => (
              <li key={item.id} className={classes.numListItem}>
                <span className={classes.numListNo} style={{ color: accent }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>
                  <span className={classes.numListTitle}>{L(item, "title")}</span>
                  {L(item, "text") && (
                    <span className={classes.numListText}>{L(item, "text")}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}

        {cards.length > 0 && (
          <div className={classes.cardRow} style={{ backgroundColor: accent }}>
            {cards.map((c, i) => (
              <div key={c.id} className={classes.numCard}>
                <span className={classes.numCardNo}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className={classes.numCardTitle}>{L(c, "title")}</h3>
                {L(c, "text") && (
                  <p className={classes.numCardText}>{L(c, "text")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderSection = (section, index) => {
    const no = numbers[index];
    const tagline = L(section, "tagline");
    const secTitle = L(section, "title");
    const lead = L(section, "lead");
    const image = mediaUrl(section.image);
    const panelTitle = L(section, "panel_title");
    const panelBody = paragraphs(L(section, "panel_body"));

    if (index === closingIndex) {
      return (
        <section key={section.id} className={classes.cta}>
          <div className={classes.ctaInner}>
            {no && (
              <span className={classes.ctaNo} style={{ color: accent }}>
                {String(no).padStart(2, "0")}
              </span>
            )}
            {tagline && <span className={classes.ctaTagline}>{tagline}</span>}
            {secTitle && <h2 className={classes.ctaTitle}>{secTitle}</h2>}
            {lead && <p className={classes.ctaLead}>{lead}</p>}
            {section.link_url && (
              <a
                className={classes.ctaBtn}
                href={section.link_url}
                style={{ backgroundColor: accent }}
              >
                {T("more")}
                <span aria-hidden="true"> →</span>
              </a>
            )}
          </div>
        </section>
      );
    }

    return (
      <section key={section.id} className={classes.block}>
        <div className={classes.blockHead}>
          <div>
            {no && (
              <span className={classes.blockNo}>{String(no).padStart(2, "0")}</span>
            )}
            {tagline && <span className={classes.tagline}>{tagline}</span>}
            {secTitle && <h2 className={classes.blockTitle}>{secTitle}</h2>}
          </div>
          {lead && <p className={classes.blockLead}>{lead}</p>}
        </div>

        {(image || panelTitle || panelBody.length > 0) && (
          <div
            className={`${classes.splitRow} ${!image ? classes.splitRowSingle : ""}`}
          >
            {image && (
              <div className={classes.splitImage}>
                <img src={image} alt="" className={classes.splitImg} />
                {(L(section, "image_eyebrow") || L(section, "image_caption")) && (
                  <div className={classes.splitCaption}>
                    {L(section, "image_eyebrow") && (
                      <span className={classes.splitCaptionEyebrow}>
                        {L(section, "image_eyebrow")}
                      </span>
                    )}
                    {L(section, "image_caption") && (
                      <span className={classes.splitCaptionTitle}>
                        {L(section, "image_caption")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {(panelTitle || panelBody.length > 0) && (
              <div className={classes.panel} style={{ backgroundColor: accent }}>
                {L(section, "panel_eyebrow") && (
                  <span className={classes.panelEyebrow}>{L(section, "panel_eyebrow")}</span>
                )}
                {panelTitle && <h3 className={classes.panelTitle}>{panelTitle}</h3>}
                {panelBody.map((t, i) => (
                  <p key={i} className={classes.panelText}>
                    {t}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {renderItems(section)}

        {section.link_url && (
          <a className={classes.blockLink} href={section.link_url}>
            {T("more")}
            <span aria-hidden="true"> ↗</span>
          </a>
        )}
      </section>
    );
  };

  return (
    <div className={classes.page}>
      {/* --------- Yuqori qism --------- */}
      <div className={classes.hero}>
        {heroImage && <img src={heroImage} alt="" className={classes.heroImg} />}
        <div className={classes.heroScrim} />

        <div className={classes.heroInner}>
          <nav className={classes.crumbs} aria-label="breadcrumb">
            <button type="button" className={classes.crumbLink} onClick={() => navigate("/")}>
              {T("home")}
            </button>
            <span aria-hidden="true">/</span>
            <button type="button" className={classes.crumbLink} onClick={() => navigate("/sdg")}>
              {T("sdg")} {goal.number}
            </button>
            <span aria-hidden="true">/</span>
            <span className={classes.crumbCurrent}>{L(goal, "title")}</span>
          </nav>

          {heroTagline && <span className={classes.heroTagline}>{heroTagline}</span>}
          <h1 className={classes.heroTitle}>{title}</h1>
          {heroLead && <p className={classes.heroLead}>{heroLead}</p>}
        </div>

        <div className={classes.badge} style={{ backgroundColor: accent }}>
          {goal.badge_prefix && (
            <span className={classes.badgePrefix}>{goal.badge_prefix}</span>
          )}
          <span className={classes.badgeNo}>{String(goal.number).padStart(2, "0")}</span>
          <span className={classes.badgeTitle}>{L(goal, "title")}</span>
        </div>
      </div>

      {/* --------- Admin kiritgan bloklar --------- */}
      {sections.map(renderSection)}

      {/* --------- CKEditor mazmuni (eski maydon) --------- */}
      {(content || (sections.length === 0 && !heroLead)) && (
        <div className={classes.narrow}>
          {contentHeading && <h2 className={classes.blockTitle}>{contentHeading}</h2>}
          <div
            className={classes.richContent}
            dangerouslySetInnerHTML={{
              __html: content || `<p>${T("soon")}</p>`,
            }}
          />
          {(author || goal.publish_date) && (
            <div className={classes.authorMeta}>
              {author && <p className={classes.authorName}>{author}</p>}
              {goal.publish_date && <p className={classes.publishDate}>{goal.publish_date}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SDGDetail;
